import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import * as Animation from "~/Animation";
import * as Color from "~/Color";
import * as Log from "~/Log";
import { DutyStatus } from "~/Log/Timeline";
import { Timeline } from "~/Timeline";

export namespace Scroll {
  export type BrushProps = {
    readonly timeline: Timeline.Remote;
    readonly milestones: readonly Timeline.Milestone.Timezone[];
    readonly containerWidth: number;
  };

  type Props = {
    readonly timeline?: Timeline.Remote;
    readonly children?: Component.Children;
  };

  export const maxWidthPixels = () => 3000 as const;
  export const marginLeftPixels = () => 32 as const;
  export const marginPixels = () => marginLeftPixels() * 2;
  export const timelinePixels = () => maxWidthPixels() - marginPixels();
  export const stepPixels = () => timelinePixels() / 24;

  export const marginLeft = () => `${marginLeftPixels()}px`;
  export const paddingBottom = () => "0.5em" as const;
  export const height = () => "0.5em" as const;

  export const use = (statusEvents: readonly Log.Event.UniqueEvent[]) => {
    const container = useRef<HTMLDivElement>(null);
    const target = useRef<HTMLDivElement>(null);
    const events = Timeline.Brusher.Controller.useFromRef(container);
    const [forceRender, toggleForceRender] = useState(false);
    const [timeout, updateTimeout] = useState<number | undefined>(undefined);

    useEffect(() => {
      const resizeHandler = () => {
        if (timeout) {
          window.clearTimeout(timeout);
        }
        const id = window.setTimeout(
          () => toggleForceRender(!forceRender),
          800
        );
        updateTimeout(id);
      };
      window.addEventListener("resize", resizeHandler);

      return () => window.removeEventListener("resize", resizeHandler);
    });

    const [containerWidth, setContainerWidth] = useState(0);
    useEffect(() => {
      if (container?.current) {
        const newWidth = container?.current.offsetWidth;
        if (newWidth !== containerWidth) {
          setContainerWidth(newWidth);
        }
      }
    }, [containerWidth]);

    return {
      events,
      target: React.useMemo(() => target, [target.current === null]),
      containerWidth,
      Scroll: ({ children, ...props }: Props) => (
        <Container ref={container} {...props}>
          <Target ref={target} {...props}>
            {children}
          </Target>
        </Container>
      ),
      Brush: ({ ...props }: BrushProps) => (
        <>
          <Timeline.Brusher
            {...props}
            background={DutyStatus.background}
            events={statusEvents}
            scrollContainer={container}
            scrollTarget={target}
          />
        </>
      ),
    };
  };

  export const Container = styled.div`
    box-sizing: border-box;
    overflow: scroll;
    overscroll-behavior: contain;
    margin-left: ${() => Timeline.Menu.width()};
    padding-bottom: ${() => paddingBottom()};

    ::-webkit-scrollbar {
      -webkit-appearance: none;
      height: 0;
      width: 0;
    }

    ::-webkit-scrollbar-thumb {
      width: 10%;
      border-radius: 2em;
      background-color: ${() => Color.translucentBlack(0.2)};
    }
  `;

  export const Target = styled.div<Props>`
    position: relative;
    margin-left: ${() => Timeline.Fade.width()};
    margin-right: ${() => Timeline.Fade.width()};
    min-width: ${() => Timeline.Scroll.marginPixels()}px;

    /* TODO: Enable smooth zooming. */
    /* ${() => Animation.Transition.styles(Animation.medium())} */

    & > div:nth-child(2) {
      margin-top: ${() => Timeline.Marker.height()};
    }

    & > div:last-of-type {
      margin-bottom: ${() => Timeline.Marker.height()};
    }
  `;

  export const BrushContainer = styled.span`
    display: inline-block;
    height: calc(1em + ${() => Timeline.Marker.height()});
    overflow: hidden;
  `;

  export type Events = {
    readonly onLeft: () => void;
    readonly onLeftMax: () => void;
    readonly onRight: () => void;
    readonly onRightMax: () => void;
    readonly onZoomIn: () => void;
    readonly onZoomOut: () => void;
    readonly onZoomReset: () => void;
  };
}
